import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import { BillRows, OverBudgetConfirm, PayButton, PayWarning, type BillSummaryProps } from './BillSummary';

type MobileBillBarProps = BillSummaryProps & {
  /** The full bill is on screen (the user scrolled to the end), so this bar steps aside. */
  hidden: boolean;
};

/**
 * Phone-only bar pinned to the bottom: the total and Pay at a glance, with the full bill one tap
 * away. It slides out of the way once the full bill at the end of the page scrolls into view.
 */
export function MobileBillBar({ hidden, ...props }: MobileBillBarProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const { total, budget, overBudget, blockedReason, updating = false, estimated = false } = props;

  const note = blockedReason
    ? blockedReason
    : budget != null
      ? overBudget
        ? `${formatMoney(total - budget)} over your ${formatMoney(budget)} budget`
        : `of your ${formatMoney(budget)} budget`
      : estimated
        ? 'Estimate'
        : '';
  const noteIsWarning = Boolean(blockedReason) || overBudget;

  // Rendered into the page body: the page wrapper is animated (it has a transform), and a fixed
  // element inside a transformed parent is positioned against that parent, not the screen.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      inert={hidden}
      aria-hidden={hidden}
      className={`fixed inset-x-0 bottom-0 z-[80] lg:hidden transition-transform duration-300 ease-out motion-reduce:transition-none ${
        hidden ? 'translate-y-full' : 'translate-y-0'
      }`}
    >
      {open ? (
        <button
          type="button"
          aria-label="Close bill"
          onClick={() => setOpen(false)}
          className="fixed inset-0 -z-10 cursor-default border-none bg-black/30"
        />
      ) : null}

      <div className="border-t border-[var(--dash-line)] bg-white/95 shadow-[0_-10px_32px_-18px_rgba(16,38,73,0.35)] backdrop-blur">
        {props.warning ? (
          <div className="px-4 pt-3 text-left">
            <PayWarning warning={props.warning} />
          </div>
        ) : null}
        {open ? (
          <div id={panelId} className="max-h-[55vh] space-y-3 overflow-y-auto px-4 pt-4 text-left">
            <BillRows {...props} />
            <OverBudgetConfirm {...props} />
          </div>
        ) : null}

        <div className="flex items-center gap-3 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            className="flex min-w-0 flex-1 items-center gap-2 border-none bg-transparent p-0 text-left"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] text-[var(--dash-muted)]">
                Total (incl. GST){open ? '' : ' · tap for bill'}
              </span>
              <span
                className={`block text-lg leading-tight font-semibold transition-opacity ${
                  overBudget ? 'text-red-600' : 'text-[var(--dash-ink)]'
                } ${updating ? 'opacity-60' : ''}`}
              >
                {formatMoney(total)}
              </span>
              {note ? (
                <span
                  className={`block truncate text-[11px] ${noteIsWarning ? 'text-red-600' : 'text-[var(--dash-muted)]'}`}
                >
                  {note}
                </span>
              ) : null}
            </span>
            <ChevronUp
              className={`h-5 w-5 shrink-0 text-[var(--dash-muted)] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>

          {props.showLaunch ? (
            <PayButton
              launchDisabled={props.launchDisabled}
              isLaunching={props.isLaunching}
              saving={props.saving}
              needsTopUp={props.needsTopUp}
              onLaunch={props.onLaunch}
              payLabel={props.payLabel}
              className="shrink-0 px-4 py-3"
            />
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
